/*
 * -----------------------------------------------------------------------
 *  <copyright file="import-dialog.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogActions, DialogContent } from '@mui/material';
import React, { useEffect, useRef } from 'react';

import Button, { CancelButton } from './buttons';
import { CustomDialog, DialogTitle } from './dialog';

interface IImportDialogParams {
    title: string;
    open: boolean;
    accept?: string;
    template: JSX.Element;
    onSelect: any;
    onClose: any;
    multiple?: boolean;
    disabled?: boolean;
    button?: any;
}

const ImportDialog = ({
    title,
    open,
    template,
    onSelect,
    onClose,
    disabled,
    multiple,
    button,
    accept,
}: IImportDialogParams) => {
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) inputRef.current.value = '';
    }, [inputRef]);

    return (
        <CustomDialog onClose={onClose} maxWidth="md" scroll="paper" fullWidth open={open}>
            <DialogTitle id="import-modal-title">
                {title}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    data-testid="close-dialog"
                    onClick={onClose}
                />
            </DialogTitle>

            <DialogContent dividers style={{ paddingLeft: 15, paddingRight: 15 }}>
                {template}
            </DialogContent>

            <DialogActions>
                <CancelButton id="import-cancel" data-testid="import-cancel" onClick={onClose} />
                {!button ? (
                    <>
                        <input
                            ref={inputRef}
                            accept={
                                accept ??
                                '.csv, .psv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                            }
                            hidden
                            multiple={multiple}
                            id="import-file-dialog"
                            type="file"
                            onChange={(event) => {
                                onSelect(event.target.files);
                                inputRef.current.value = '';
                            }}
                        />
                        <label htmlFor="import-file-dialog">
                            <Button
                                id="import-file-select"
                                component="span"
                                disabled={disabled}
                                text={multiple ? 'common:selectFiles' : 'common:selectFile'}
                                data-testid="import-file-select"
                            />
                        </label>
                    </>
                ) : (
                    button
                )}
            </DialogActions>
        </CustomDialog>
    );
};

export default ImportDialog;
